import {Dispatch, SetStateAction, useCallback, useState} from "react";

type DialogManagerHook<T> = {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    data: T | undefined;
    setData: Dispatch<SetStateAction<T | undefined>>;
    updateState: (value: Partial<DialogManagerState<T>>) => void;
};

type DialogManagerState<T> = {
    isOpen: boolean;
    data: T | undefined;
};

/**
 * Simple hook that stores everything needed for dialog management.
 * Best used when the dialog needs data passed to it, and you want to avoid
 * mulitple useState calls.
 * */
const useDialogManager = <T>(initialData: T): DialogManagerHook<T> => {
    const [state, setState] = useState<DialogManagerState<T>>({
        isOpen: false,
        data: initialData,
    });

    const open = useCallback(() => {
        setState((s) => ({...s, isOpen: true}));
    }, []);

    const close = useCallback(() => {
        setState((s) => ({...s, isOpen: false}));
    }, []);

    const setData = useCallback((value: SetStateAction<T | undefined>) => {
        if (typeof value === "function") {
            setState((s) => ({...s, data: (value as (prev: T | undefined) => T | undefined)(s.data)}));
        } else {
            setState((s) => ({...s, data: value}));
        }
    }, []);

    const updateState = useCallback((value: Partial<DialogManagerState<T>>) => {
        setState((s) => ({...s, ...value}));
    }, []);

    return {isOpen: state.isOpen, open, close, data: state.data, setData, updateState};
};

export default useDialogManager;
